import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Filter, 
  BarChart2, TrendingUp, Calendar, BookOpen, 
  Edit3, Mic, Brain, ChevronLeft, ArrowUp, 
  ArrowDown, Sparkles, Activity, Clock, Target, 
  ChevronRight, Award, Flame, Type, Library, MessagesSquare,
  Play, ChevronDown, CheckCircle2, Volume2, ArrowRight, X, Headphones, Compass, Crosshair
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, 
  ResponsiveContainer, Cell, AreaChart, Area 
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { statsService } from '../services/statsService';
import { uniqueWordsService } from '../services/uniqueWordsService';
import { dbManager } from '../services/db_manager';
import { tourService } from '../services/tourService';
import { STRICT_LANGUAGES } from '../constants';
import { FloatingTourButton } from '../components/FloatingTourButton';
import { WrappedModal } from '../components/WrappedModal';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { Orb } from '../components/HomeOrbOverview';
import { SECTION_ICONS, metricIcon, metricMotion } from '../components/sectionIcons';
import { profileService } from '../services/profileService';
import { textToSpeechService } from '../services/textToSpeechService';
import T from '../components/T';
import i18nInstance from '../i18n';


const LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  hu: '🇭🇺',
  ru: '🇷🇺',
  pt: '🇧🇷'
};

const StatisticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useData() as any;

  const [logs, setLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<'daily' | 'weekly' | 'monthly' | 'total'>('total');
  const chartRef = useRef<HTMLDivElement>(null);
  const detailedSectionRef = useRef<HTMLDivElement>(null);
  const focusSectionRef = useRef<HTMLDivElement>(null);

  const [activeMetric, setActiveMetric] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'summary' | 'flow'>('summary');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterLang, setFilterLang] = useState('all');
  const [tempLang, setTempLang] = useState('all');
  const [tempPeriod, setTempPeriod] = useState<'Dias' | 'Semanas' | 'Anos'>('Dias');
  const [filteredCounts, setFilteredCounts] = useState<{writing: number, pronunciation: number, flashcards: number, quiz_correct?: number, quiz_wrong?: number} | null>(null);
  const [availableLangs, setAvailableLangs] = useState<{code: string, name: string}[]>([]);
  const [uniqueWordsCount, setUniqueWordsCount] = useState<number>(0);
  const [reportData, setReportData] = useState<any>(null);
  const [isWrappedOpen, setIsWrappedOpen] = useState(false);
  const [errorTab, setErrorTab] = useState<'mastered' | 'pending'>('mastered');
  // Design 2.0: orbes no topo abrem uma seção por vez (igual à Home); ícone anima no hover do cartão
  const [openPanel, setOpenPanel] = useState<'summary' | 'skills' | 'errors' | null>('summary');
  const [hoverMetric, setHoverMetric] = useState<string | null>(null);
  const [dbProfile, setDbProfile] = useState<any>(null);
  const [chartPageIndex, setChartPageIndex] = useState<number>(0);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string>('');
  const [shadowingTotalSeconds, setShadowingTotalSeconds] = useState<number>(0);
  const [shadowingDailyMap, setShadowingDailyMap] = useState<Record<string, number>>({});
  const [dailyTempoMap, setDailyTempoMap] = useState<Record<string, number>>({});
  const [dailyPrecisaoMap, setDailyPrecisaoMap] = useState<Record<string, number>>({});
  const [dailyConsistenciaMap, setDailyConsistenciaMap] = useState<Record<string, number>>({});

  useEffect(() => {
    async function loadShadowingStats() {
      if (!user?.id) return;
      try {
        const cleanFilterLang = filterLang === 'all' ? null : filterLang.split(/[-_]/)[0].toLowerCase();
        const filters: Record<string, any> = { user_id: user.id };
        if (cleanFilterLang) {
          filters.target_lang = cleanFilterLang;
        }

        const rows: any = await dbManager.from('shadowing_logs', user.id).select(filters);
        if (Array.isArray(rows)) {
          const today = new Date();
          const getLocalISOString = (d: Date) => {
            const date = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
            return date.toISOString().split('T')[0];
          };
          const todayStr = getLocalISOString(today);

          let minDateStr = '';
          if (timeframe === 'daily') {
            minDateStr = todayStr;
          } else if (timeframe === 'weekly') {
            const d = new Date(today);
            d.setDate(d.getDate() - 6);
            minDateStr = getLocalISOString(d);
          } else if (timeframe === 'monthly') {
            const d = new Date(today);
            d.setDate(d.getDate() - 29);
            minDateStr = getLocalISOString(d);
          }

          let filtered = rows;
          if (minDateStr) {
            if (timeframe === 'daily') {
              filtered = rows.filter((r: any) => r.activity_date === todayStr);
            } else {
              filtered = rows.filter((r: any) => r.activity_date >= minDateStr);
            }
          }

          const totalSec = filtered.reduce((acc: number, r: any) => acc + (Number(r.duration) || 0), 0);
          setShadowingTotalSeconds(totalSec);

          const dailyMap: Record<string, number> = {};
          rows.forEach((r: any) => {
            if (r.activity_date && r.duration) {
              dailyMap[r.activity_date] = (dailyMap[r.activity_date] || 0) + Number(r.duration);
            }
          });
          setShadowingDailyMap(dailyMap);
        } else {
          setShadowingTotalSeconds(0);
          setShadowingDailyMap({});
        }
      } catch (err) {
        console.warn('[StatisticsPage] Erro ao carregar logs de shadowing:', err);
      }
    }

    async function loadReportMetricLogs() {
      if (!user?.id) return;
      try {
        const cleanFilterLang = filterLang === 'all' ? null : filterLang.split(/[-_]/)[0].toLowerCase();
        const baseFilters: Record<string, any> = { user_id: user.id };

        const [tRows, pRows, cRows]: [any, any, any] = await Promise.all([
          dbManager.from('logs_tempo_estimado', user.id).select(baseFilters).catch(() => []),
          dbManager.from('logs_taxa_precisao', user.id).select(baseFilters).catch(() => []),
          dbManager.from('logs_consistencia', user.id).select(baseFilters).catch(() => [])
        ]);

        const tempoMap: Record<string, number> = {};
        if (Array.isArray(tRows)) {
          tRows.forEach((r: any) => {
            const date = r.activity_date || r.local_date;
            const rowLang = (r.target_lang || r.lang_code || '').toLowerCase();
            if (cleanFilterLang && rowLang !== cleanFilterLang && rowLang !== 'all') return;
            const mins = Number(r.minutes) || Number(r.estimated_minutes) || 0;
            if (date) {
              tempoMap[date] = (tempoMap[date] || 0) + mins;
            }
          });
        }
        setDailyTempoMap(tempoMap);

        const precisaoMap: Record<string, number> = {};
        if (Array.isArray(pRows)) {
          pRows.forEach((r: any) => {
            const date = r.activity_date || r.local_date;
            const rowLang = (r.target_lang || r.lang_code || '').toLowerCase();
            if (cleanFilterLang && rowLang !== cleanFilterLang && rowLang !== 'all') return;
            const pct = r.accuracy_percent !== undefined ? Number(r.accuracy_percent) : (r.accuracy_percentage !== undefined ? Number(r.accuracy_percentage) : 0);
            if (date) {
              precisaoMap[date] = pct;
            }
          });
        }
        setDailyPrecisaoMap(precisaoMap);

        const consistenciaMap: Record<string, number> = {};
        if (Array.isArray(cRows)) {
          cRows.forEach((r: any) => {
            const date = r.activity_date || r.local_date;
            const rowLang = (r.target_lang || r.lang_code || '').toLowerCase();
            if (cleanFilterLang && rowLang !== cleanFilterLang && rowLang !== 'all') return;
            const pct = r.consistency_percent !== undefined ? Number(r.consistency_percent) : (r.consistency_percentage !== undefined ? Number(r.consistency_percentage) : 0);
            if (date) {
              consistenciaMap[date] = pct;
            }
          });
        }
        setDailyConsistenciaMap(consistenciaMap);
      } catch (err) {
        console.warn('[StatisticsPage] Erro ao carregar logs adicionais de relatórios:', err);
      }
    }

    loadShadowingStats();
    loadReportMetricLogs();
  }, [user?.id, filterLang, timeframe]);

  const shadowingDisplay = useMemo(() => {
    if (shadowingTotalSeconds <= 0) return { val: '0', unit: 'min' };
    const mins = Math.round(shadowingTotalSeconds / 60);
    if (mins < 1) return { val: '< 1', unit: 'min' };
    if (mins >= 60) {
      const hours = (shadowingTotalSeconds / 3600).toFixed(1);
      return { val: hours, unit: 'h' };
    }
    return { val: `${mins}`, unit: 'min' };
  }, [shadowingTotalSeconds]);

  useEffect(() => {
    async function loadUserProfile() {
      if (!user?.id) return;
      try {
        const p = await profileService.getProfile(user.id);
        if (p) setDbProfile(p);
      } catch (err) {
        console.error('[StatisticsPage] Erro ao carregar perfil do D1:', err);
      }
    }
    loadUserProfile();
  }, [user?.id]);

  const [voiceTotalsCache, setVoiceTotalsCache] = useState<Record<string, number>>(() => {
    try {
      const cached = localStorage.getItem('libritui_voice_totals_cache');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });

  const [voiceChartData, setVoiceChartData] = useState<{date: string, minutes: number}[]>([]);

  const metricConfig: Record<string, { label: string, color: string, icon: any, key: string, isTime?: boolean }> = {
    'books_read': { label: t('statistics.metrics.books_read', 'LIVROS LIDOS'), color: '#B08D57', icon: metricIcon('books_read'), key: 'books_read' },
    'pages_read': { label: t('statistics.metrics.reading', 'LEITURA'), color: '#5E7FA3', icon: metricIcon('pages_read'), key: 'pages_read' },
    'unique_words': { label: t('statistics.metrics.unique_words', 'PALAVRAS ÚNICAS'), color: '#5E7FA3', icon: metricIcon('unique_words'), key: 'unique_words' },
    'words_written': { label: t('statistics.metrics.writing', 'ESCRITA'), color: '#B5525E', icon: metricIcon('words_written'), key: 'words_written' },
    'pronunciation_count': { label: t('statistics.metrics.pronunciation', 'PRONÚNCIA'), color: '#5E7FA3', icon: metricIcon('pronunciation_count'), key: 'pronunciation_count' },
    'active_voice_duration': { label: t('statistics.metrics.active_voice', 'VOZ ATIVA'), color: '#8E9668', icon: metricIcon('active_voice_duration'), key: 'active_voice_duration', isTime: true },
    'cards_reviewed': { label: t('statistics.metrics.flashcards', 'FLASHCARDS'), color: '#d3916e', icon: metricIcon('cards_reviewed'), key: 'cards_reviewed' },
    'total_quizzes_attempted': { label: t('statistics.metrics.grammar', 'GRAMÁTICA'), color: '#8E9668', icon: metricIcon('total_quizzes_attempted'), key: 'total_quizzes_attempted' },
    'listening_count': { label: 'LISTENING', color: '#9AA6B8', icon: metricIcon('listening_count'), key: 'listening_count' },
    'shadowing_duration': { label: 'SHADOWING', color: '#9AA6B8', icon: metricIcon('shadowing_duration'), key: 'shadowing_duration', isTime: true },
    'tempo_estimado': { label: 'TEMPO EST.', color: '#8E9668', icon: metricIcon('tempo_estimado'), key: 'tempo_estimado', isTime: true },
    'taxa_precisao': { label: 'PRECISÃO', color: '#B08D57', icon: metricIcon('taxa_precisao'), key: 'taxa_precisao' },
    'consistencia': { label: 'CONSISTÊNCIA', color: '#B5525E', icon: metricIcon('consistencia'), key: 'consistencia' }
  };

  useEffect(() => {
    async function loadAllUserLanguages() {
      const langSet = new Set<string>();

      if (profile?.learning_languages && Array.isArray(profile.learning_languages)) {
        profile.learning_languages.forEach((l: string) => {
          if (l) langSet.add(l.toLowerCase());
        });
      }

      if (user?.id) {
        try {
          const uniqueWordsLangs = await uniqueWordsService.getAllLanguageUniqueWords(user.id);
          Object.keys(uniqueWordsLangs).forEach(l => {
            if (l) langSet.add(l.toLowerCase());
          });

          const logsRes = await dbManager.from('user_daily_logs', user.id).select({ user_id: user.id });
          if (Array.isArray(logsRes)) {
            logsRes.forEach((r: any) => {
              if (r.lang_code && r.lang_code !== 'all') {
                const code = r.lang_code.split('-')[0].toLowerCase();
                langSet.add(code);
              }
            });
          }

          const decksRes = await dbManager.from('decks', user.id).select({ user_id: user.id });
          if (Array.isArray(decksRes)) {
            decksRes.forEach((d: any) => {
              if (d.lang_code) langSet.add(d.lang_code.split('-')[0].toLowerCase());
            });
          }
          const importedRes = await dbManager.from('flashcards_imported', user.id).select({ user_id: user.id });
          if (Array.isArray(importedRes)) {
            importedRes.forEach((d: any) => {
              if (d.idioma_alvo) langSet.add(d.idioma_alvo.split('-')[0].toLowerCase());
            });
          }
          const shadowRes = await dbManager.from('shadowing_logs', user.id).select({ user_id: user.id });
          if (Array.isArray(shadowRes)) {
            shadowRes.forEach((d: any) => {
              if (d.target_lang) langSet.add(d.target_lang.split(/[-_]/)[0].toLowerCase());
            });
          }
        } catch (err) {
          console.error('[StatisticsPage] Erro ao carregar idiomas do usuário:', err);
        }
      }

      const formatted = Array.from(langSet).map(code => {
        const match = STRICT_LANGUAGES.find(s => s.code.toLowerCase() === code.toLowerCase());
        const display = match ? `${code.toUpperCase()} (${match.name})` : code.toUpperCase();
        return { code, name: display };
      });

      if (formatted.length > 0) {
        setAvailableLangs(formatted);
      } else {
        setAvailableLangs([
          { code: 'en', name: 'EN (Inglês)' },
          { code: 'fr', name: 'FR (Francês)' },
          { code: 'es', name: 'ES (Espanhol)' },
          { code: 'de', name: 'DE (Alemão)' },
          { code: 'it', name: 'IT (Italiano)' }
        ]);
      }
    }

    loadAllUserLanguages();
  }, [user?.id, profile]);

  const [dailyWordStatsMap, setDailyWordStatsMap] = useState<Record<string, number>>({});

  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    if (!user?.id) return;
    setIsLoading(true);

    try {
      const dailyWordsMap = await uniqueWordsService.getDailyWordStats(user.id, filterLang);
      setDailyWordStatsMap(dailyWordsMap || {});

      const cacheKey = `dashboard_stats_${filterLang}_${timeframe}`;
      const isDirty = localStorage.getItem('libritui_stats_dirty_FLAG') === 'true';

      if (!forceRefresh && !isDirty) {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          setLogs(parsed.logs || []);
          setFilteredCounts(parsed.filtered_stats || null);
          
          if (Array.isArray(parsed.voice_chart)) {
            const dataForChart = parsed.voice_chart.map((row: any) => ({
              name: row.name || row.date,
              minutos: row.minutos || row.minutes || 0
            }));
            setVoiceChartData(dataForChart);
          } else {
            setVoiceChartData([]);
          }
          
          setVoiceTotalsCache(parsed.voice_totals || {});
          setIsLoading(false);
          return;
        }
      }

      if (isDirty) {
        Object.keys(localStorage).forEach(k => {
          if (k.startsWith('dashboard_stats_')) {
            localStorage.removeItem(k);
          }
        });
      }

      statsService.setLangFilter(filterLang);
      const results = await statsService.getDashboardStats(user.id, filterLang, timeframe);

      if (results) {
        setLogs(results.logs || []);
        setFilteredCounts(results.filtered_stats || null);
        
        if (Array.isArray(results.voice_chart)) {
          const dataForChart = results.voice_chart.map((row: any) => ({
            name: row.date,
            minutos: row.minutes
          }));
          setVoiceChartData(dataForChart);
        }

        if (results.voice_totals) {
          setVoiceTotalsCache(results.voice_totals);
        }

        localStorage.setItem(cacheKey, JSON.stringify(results));
        localStorage.setItem('libritui_stats_dirty_FLAG', 'false');
      }
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filterLang, timeframe]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch Report Analytics (Precision, Consistency, Skill Radar, Mastered/Pending Errors)
  const fetchAnalyticsPeriodReport = useCallback(async () => {
    if (!user?.id) return;
    try {
      const targetLang = filterLang === 'all' ? (profile?.studyLanguage || profile?.targetLanguage || 'en') : filterLang;
      const periodParam = timeframe === 'total' ? 'monthly' : timeframe;
      const res = await fetch('/api/db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_analytics_period_report',
          user_id: user.id,
          data: {
            period: periodParam,
            target_lang: targetLang
          }
        })
      });
      const json = await res.json();
      if (json.success) {
        setReportData(json);
      }
    } catch (e) {
      console.error('Error fetching analytics period report:', e);
    }
  }, [user?.id, timeframe, filterLang, profile]);

  useEffect(() => {
    fetchAnalyticsPeriodReport();
  }, [fetchAnalyticsPeriodReport]);

  useEffect(() => {
    const handleUpdate = (e: any) => {
      const { field, amount, date } = e.detail || {};
      if (typeof setLogs === 'function') {
        setLogs(prevLogs => {
          const newLogs = [...prevLogs];
          const logIdx = newLogs.findIndex(l => l.activity_date === date);
          
          if (logIdx !== -1) {
            newLogs[logIdx] = { 
              ...newLogs[logIdx], 
              [field]: (newLogs[logIdx][field] || 0) + (amount || 0) 
            };
          } else {
            newLogs.unshift({
              activity_date: date,
              pages_read: 0,
              words_written: 0,
              pronunciation_count: 0,
              cards_reviewed: 0,
              books_read: 0,
              active_voice_duration: 0,
              total_quizzes_attempted: 0,
              [field]: amount || 0
            });
          }
          return newLogs;
        });
      }
    };

    const handleSynced = () => {
      fetchDashboardData(true);
      fetchAnalyticsPeriodReport();
    };

    window.addEventListener('libritui-stats-updated', handleUpdate);
    window.addEventListener('libritui-stats-synced', handleSynced);
    window.addEventListener('libritui-unique-words-updated', handleSynced);
    
    return () => {
      window.removeEventListener('libritui-stats-updated', handleUpdate);
      window.removeEventListener('libritui-stats-synced', handleSynced);
      window.removeEventListener('libritui-unique-words-updated', handleSynced);
    };
  }, [user, fetchDashboardData, fetchAnalyticsPeriodReport]);

  useEffect(() => {
    let isMounted = true;
    const loadUniqueWords = async () => {
      if (!user?.id) return;
      try {
        const count = await uniqueWordsService.getUniqueWordsCount(user.id, filterLang);
        if (isMounted) {
          setUniqueWordsCount(count);
        }
      } catch (err) {
        console.error("Error loading unique words count:", err);
      }
    };
    loadUniqueWords();
    return () => { isMounted = false; };
  }, [user?.id, filterLang]);

  const periodAnalytics = useMemo(() => {
    const today = new Date();
    const getLocalISOString = (date: Date) => {
      const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return d.toISOString().split('T')[0];
    };
    const todayStr = getLocalISOString(today);

    const normalizeDbDate = (dbDateStr: string) => {
      const d = new Date(dbDateStr + 'T12:00:00Z');
      return d.toISOString().split('T')[0];
    };

    let currentLogs: any[] = [];
    let periodDays = 1;

    if (timeframe === 'daily') {
      periodDays = 1;
      currentLogs = logs.filter(l => {
        const d = normalizeDbDate(l.activity_date);
        return d === todayStr;
      });
    } else if (timeframe === 'weekly') {
      periodDays = 7;
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - 6);
      const wStartStr = getLocalISOString(weekStart);
      currentLogs = logs.filter(l => normalizeDbDate(l.activity_date) >= wStartStr);
    } else if (timeframe === 'monthly') {
      periodDays = 30;
      const monthStart = new Date(today);
      monthStart.setDate(monthStart.getDate() - 29);
      const mStartStr = getLocalISOString(monthStart);
      currentLogs = logs.filter(l => normalizeDbDate(l.activity_date) >= mStartStr);
    } else {
      periodDays = 30;
      currentLogs = logs;
    }

    const pages = currentLogs.reduce((a, l) => a + (l.pages_read || 0), 0);
    const voiceSec = currentLogs.reduce((a, l) => a + (l.active_voice_duration || 0), 0);
    const voiceMin = Math.round(voiceSec / 60);
    const cards = currentLogs.reduce((a, l) => a + (l.cards_reviewed || 0), 0);
    const writing = currentLogs.reduce((a, l) => a + (l.words_written || 0), 0);
    const pronunciation = currentLogs.reduce((a, l) => a + (l.pronunciation_count || 0), 0);
    const quizzes = currentLogs.reduce((a, l) => a + (l.total_quizzes_attempted || 0), 0);
    const correct = currentLogs.reduce((a, l) => a + (l.total_correct_answers || 0), 0);
    const wrong = currentLogs.reduce((a, l) => a + (l.total_wrong_answers || 0), 0);

    const uniqueWords = currentLogs.reduce((a, l) => {
      const dailyCount = dailyWordStatsMap[l.activity_date] !== undefined ? dailyWordStatsMap[l.activity_date] : (l.unique_words || 0);
      return a + dailyCount;
    }, 0);

    const listening = currentLogs.reduce((a, l) => a + (l.listening_count || 0), 0);
    const hasActivity = currentLogs.length > 0 && (pages > 0 || voiceMin > 0 || cards > 0 || writing > 0 || pronunciation > 0 || quizzes > 0 || uniqueWords > 0 || listening > 0);

    // 1. Estimated Practice Minutes
    const equivalent_minutes = hasActivity
      ? Math.round((pages * 2.5) + voiceMin + (cards * 0.5) + (writing * 3.0) + (pronunciation * 1.0) + (quizzes * 1.5) + (listening * 1.0))
      : 0;

    // 2. Accuracy Rate
    let accuracy_rate = 0;
    const attempts = correct + wrong;
    if (attempts > 0) {
      accuracy_rate = Math.round((correct / attempts) * 100);
    } else if (hasActivity) {
      accuracy_rate = reportData?.metrics?.accuracy_rate || 92;
    } else {
      accuracy_rate = 0;
    }

    // 3. Consistency
    let consistency_percent = 0;
    if (timeframe === 'total') {
      consistency_percent = reportData?.metrics?.consistency_percent || (hasActivity ? 100 : 0);
    } else if (timeframe === 'daily') {
      consistency_percent = hasActivity ? 100 : 0;
    } else {
      const activeDays = new Set(currentLogs.map(l => l.activity_date)).size;
      consistency_percent = Math.min(100, Math.round((activeDays / periodDays) * 100));
    }

    // 4. Skill Radar
    const skill_radar = {
      reading: pages > 0 ? Math.min(100, Math.max(15, Math.round((pages / Math.max(1, periodDays * 3)) * 100))) : 0,
      vocabulary: (uniqueWords > 0 || cards > 0) ? Math.min(100, Math.max(15, Math.round(((uniqueWords * 5 + cards) / Math.max(1, periodDays * 15)) * 100))) : 0,
      active_voice: voiceMin > 0 ? Math.min(100, Math.max(15, Math.round((voiceMin / Math.max(1, periodDays * 2)) * 100))) : 0,
      writing: writing > 0 ? Math.min(100, Math.max(15, Math.round(((writing * 10) / Math.max(1, periodDays * 5)) * 100))) : 0,
      pronunciation: pronunciation > 0 ? Math.min(100, Math.max(15, Math.round(((pronunciation * 10) / Math.max(1, periodDays * 5)) * 100))) : 0,
      listening: listening > 0 ? Math.min(100, Math.max(15, Math.round(((listening * 10) / Math.max(1, periodDays * 5)) * 100))) : (reportData?.metrics?.skill_radar?.listening || 0)
    };

    return {
      equivalent_minutes,
      accuracy_rate,
      consistency_percent,
      skill_radar,
      uniqueWords,
      hasActivity
    };
  }, [logs, timeframe, dailyWordStatsMap, reportData]);

  const stats = useMemo(() => {
    if (!logs.length) return {
      books_read: { current: 0, change: 0 },
      pages_read: { current: 0, change: 0 },
      unique_words: { current: timeframe === 'total' ? uniqueWordsCount : 0, change: 0 },
      words_written: { current: 0, change: 0 },
      pronunciation_count: { current: 0, change: 0 },
      active_voice_duration: { current: 0, change: 0 },
      cards_reviewed: { current: 0, change: 0 },
      total_quizzes_attempted: { current: 0, change: 0 },
      listening_count: { current: 0, change: 0 },
      tempo_estimado: { current: 0, change: 0 },
      taxa_precisao: { current: 0, change: 0 },
      consistencia: { current: 0, change: 0 }
    };

    const metrics = ['books_read', 'pages_read', 'words_written', 'pronunciation_count', 'active_voice_duration', 'cards_reviewed', 'total_quizzes_attempted', 'listening_count'];
    const result: any = {};

    const today = new Date();
    const getLocalISOString = (date: Date) => {
      const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return d.toISOString().split('T')[0];
    };
    const todayStr = getLocalISOString(today);

    let currentLogs: any[] = [];
    let previousLogs: any[] = [];

    const normalizeDbDate = (dbDateStr: string) => {
      const d = new Date(dbDateStr + 'T12:00:00Z');
      return d.toISOString().split('T')[0];
    };

    if (timeframe === 'daily') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = getLocalISOString(yesterday);
      
      currentLogs = logs.filter(l => normalizeDbDate(l.activity_date) === todayStr);
      previousLogs = logs.filter(l => normalizeDbDate(l.activity_date) === yesterdayStr);
    } else if (timeframe === 'weekly') {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - 6);
      const wStartStr = getLocalISOString(weekStart);

      const prevWeekStart = new Date(weekStart);
      prevWeekStart.setDate(prevWeekStart.getDate() - 7);
      const prevWStartStr = getLocalISOString(prevWeekStart);

      currentLogs = logs.filter(l => normalizeDbDate(l.activity_date) >= wStartStr);
      previousLogs = logs.filter(l => {
        const d = normalizeDbDate(l.activity_date);
        return d >= prevWStartStr && d < wStartStr;
      });
    } else if (timeframe === 'monthly') {
      const monthStart = new Date(today);
      monthStart.setDate(monthStart.getDate() - 29);
      const mStartStr = getLocalISOString(monthStart);

      const prevMonthStart = new Date(monthStart);
      prevMonthStart.setDate(prevMonthStart.getDate() - 30);
      const prevMStartStr = getLocalISOString(prevMonthStart);

      currentLogs = logs.filter(l => normalizeDbDate(l.activity_date) >= mStartStr);
      previousLogs = logs.filter(l => {
        const d = normalizeDbDate(l.activity_date);
        return d >= prevMStartStr && d < mStartStr;
      });
    } else if (timeframe === 'total') {
      currentLogs = logs;
      previousLogs = [];
    }

    metrics.forEach(metric => {
      let currentVal = currentLogs.reduce((acc, l) => acc + (l[metric] || 0), 0);
      let previousVal = previousLogs.reduce((acc, l) => acc + (l[metric] || 0), 0);

      if (metric === 'books_read') {
        const reportBooks = reportData?.metrics?.books_read || 0;
        currentVal = Math.max(currentVal, reportBooks);
      }

      let change = 0;
      if (previousVal === 0) {
        change = currentVal > 0 ? 100 : 0;
      } else {
        change = Math.round(((currentVal - previousVal) / previousVal) * 100);
      }
      
      result[metric] = { current: currentVal, change };
    });

    result['unique_words'] = { 
      current: timeframe === 'total' ? uniqueWordsCount : periodAnalytics.uniqueWords, 
      change: 0 
    };

    result['tempo_estimado'] = { current: periodAnalytics.equivalent_minutes, change: 0 };
    result['taxa_precisao'] = { current: periodAnalytics.accuracy_rate, change: 0 };
    result['consistencia'] = { current: periodAnalytics.consistency_percent, change: 0 };

    return result;
  }, [logs, timeframe, filterLang, uniqueWordsCount, voiceTotalsCache, periodAnalytics]);

  const chartData = useMemo(() => {
    const mergedLogsMap = new Map();
    
    [...logs].forEach(log => {
      mergedLogsMap.set(log.activity_date, { ...log });
    });

    [dailyWordStatsMap, shadowingDailyMap, dailyTempoMap, dailyPrecisaoMap, dailyConsistenciaMap].forEach(mapObj => {
      if (mapObj && typeof mapObj === 'object') {
        Object.keys(mapObj).forEach(d => {
          if (!mergedLogsMap.has(d)) {
            mergedLogsMap.set(d, { activity_date: d });
          }
        });
      }
    });
    
    const validLogs = Array.from(mergedLogsMap.values()).filter(l => !!l.activity_date);
    const sorted = validLogs.sort((a, b) => a.activity_date.localeCompare(b.activity_date));
    
    const dailyItems = sorted.map(log => {
      const dateObj = new Date(log.activity_date + 'T12:00:00');
      const dailyUniqueWords = dailyWordStatsMap[log.activity_date] !== undefined
        ? dailyWordStatsMap[log.activity_date]
        : (log.unique_words || 0);

      const shadowingSec = shadowingDailyMap[log.activity_date] || 0;
      const shadowingMin = shadowingSec > 0 ? Number((shadowingSec / 60).toFixed(1)) : 0;

      const fallbackEstMin = Number((( (log.pages_read || 0) * 1.5 + (log.words_written || 0) * 0.5 + (log.active_voice_duration || 0) / 60 + shadowingMin ).toFixed(1)));
      const dayTempo = dailyTempoMap[log.activity_date] !== undefined
        ? Number(dailyTempoMap[log.activity_date].toFixed(1))
        : fallbackEstMin;

      const hasActivityOnDay = (log.pages_read || 0) > 0 || (log.words_written || 0) > 0 || (log.pronunciation_count || 0) > 0 || (log.cards_reviewed || 0) > 0 || (log.listening_count || 0) > 0 || (log.total_quizzes_attempted || 0) > 0 || shadowingMin > 0;

      const dayPrecisao = dailyPrecisaoMap[log.activity_date] !== undefined
        ? Number(dailyPrecisaoMap[log.activity_date].toFixed(0))
        : (hasActivityOnDay ? 100 : 0);

      const dayConsistencia = dailyConsistenciaMap[log.activity_date] !== undefined
        ? Number(dailyConsistenciaMap[log.activity_date].toFixed(0))
        : (hasActivityOnDay ? 100 : 0);

      const minutes = log.active_voice_duration ? Number((Number(log.active_voice_duration) / 60).toFixed(1)) : 0;

      return {
        date: log.activity_date,
        dateObj,
        hasActivity: hasActivityOnDay,
        books_read: log.books_read || 0,
        pages_read: log.pages_read || 0,
        words_written: log.words_written || 0,
        pronunciation_count: log.pronunciation_count || 0,
        minutos: minutes,
        active_voice_duration: minutes,
        cards_reviewed: log.cards_reviewed || 0,
        listening_count: log.listening_count || 0,
        total_quizzes_attempted: log.total_quizzes_attempted || 0,
        unique_words: dailyUniqueWords,
        shadowing_duration: shadowingMin,
        shadowing_minutos: shadowingMin,
        tempo_estimado: dayTempo,
        taxa_precisao: dayPrecisao,
        consistencia: dayConsistencia
      };
    });

    if (timeframe === 'weekly') {
      const getISOWeek = (dObj: Date) => {
        const date = new Date(dObj.getTime());
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
        const week1 = new Date(date.getFullYear(), 0, 4);
        return Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7) + 1;
      };

      const weekGroups = new Map<string, typeof dailyItems>();
      dailyItems.forEach(item => {
        const wNum = getISOWeek(item.dateObj);
        const wKey = `${item.dateObj.getFullYear()}-W${wNum.toString().padStart(2, '0')}`;
        if (!weekGroups.has(wKey)) {
          weekGroups.set(wKey, []);
        }
        weekGroups.get(wKey)!.push(item);
      });

      return Array.from(weekGroups.entries()).map(([wKey, items]) => {
        const wNum = wKey.split('-W')[1] || '';
        const books_read = items.reduce((a, i) => a + i.books_read, 0);
        const pages_read = items.reduce((a, i) => a + i.pages_read, 0);
        const words_written = items.reduce((a, i) => a + i.words_written, 0);
        const pronunciation_count = items.reduce((a, i) => a + i.pronunciation_count, 0);
        const active_voice_duration = Number(items.reduce((a, i) => a + i.active_voice_duration, 0).toFixed(1));
        const cards_reviewed = items.reduce((a, i) => a + i.cards_reviewed, 0);
        const listening_count = items.reduce((a, i) => a + i.listening_count, 0);
        const total_quizzes_attempted = items.reduce((a, i) => a + i.total_quizzes_attempted, 0);
        const unique_words = items.reduce((a, i) => a + i.unique_words, 0);
        const shadowing_duration = Number(items.reduce((a, i) => a + i.shadowing_duration, 0).toFixed(1));
        const tempo_estimado = Number(items.reduce((a, i) => a + i.tempo_estimado, 0).toFixed(1));

        const activeDays = items.filter(i => i.hasActivity || i.taxa_precisao > 0);
        const taxa_precisao = activeDays.length > 0
          ? Math.round(activeDays.reduce((a, i) => a + i.taxa_precisao, 0) / activeDays.length)
          : 0;

        const consistencia = items.length > 0
          ? Math.min(100, Math.round((items.filter(i => i.hasActivity).length / 7) * 100))
          : 0;

        const label = `Sem ${wNum}`;
        return {
          name: label,
          date: wKey,
          label,
          books_read,
          pages_read,
          words_written,
          pronunciation_count,
          minutos: active_voice_duration,
          active_voice_duration,
          cards_reviewed,
          listening_count,
          total_quizzes_attempted,
          unique_words,
          shadowing_duration,
          shadowing_minutos: shadowing_duration,
          tempo_estimado,
          taxa_precisao,
          consistencia,
          total: books_read + pages_read + words_written + pronunciation_count + active_voice_duration + cards_reviewed + listening_count + total_quizzes_attempted + unique_words + shadowing_duration
        };
      });
    }

    if (timeframe === 'yearly') {
      const monthGroups = new Map<string, typeof dailyItems>();
      dailyItems.forEach(item => {
        const mKey = `${item.dateObj.getFullYear()}-${(item.dateObj.getMonth() + 1).toString().padStart(2, '0')}`;
        if (!monthGroups.has(mKey)) {
          monthGroups.set(mKey, []);
        }
        monthGroups.get(mKey)!.push(item);
      });

      return Array.from(monthGroups.entries()).map(([mKey, items]) => {
        const dObj = items[0].dateObj;
        const label = dObj.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }).replace('.', '');
        const books_read = items.reduce((a, i) => a + i.books_read, 0);
        const pages_read = items.reduce((a, i) => a + i.pages_read, 0);
        const words_written = items.reduce((a, i) => a + i.words_written, 0);
        const pronunciation_count = items.reduce((a, i) => a + i.pronunciation_count, 0);
        const active_voice_duration = Number(items.reduce((a, i) => a + i.active_voice_duration, 0).toFixed(1));
        const cards_reviewed = items.reduce((a, i) => a + i.cards_reviewed, 0);
        const listening_count = items.reduce((a, i) => a + i.listening_count, 0);
        const total_quizzes_attempted = items.reduce((a, i) => a + i.total_quizzes_attempted, 0);
        const unique_words = items.reduce((a, i) => a + i.unique_words, 0);
        const shadowing_duration = Number(items.reduce((a, i) => a + i.shadowing_duration, 0).toFixed(1));
        const tempo_estimado = Number(items.reduce((a, i) => a + i.tempo_estimado, 0).toFixed(1));

        const activeDays = items.filter(i => i.hasActivity || i.taxa_precisao > 0);
        const taxa_precisao = activeDays.length > 0
          ? Math.round(activeDays.reduce((a, i) => a + i.taxa_precisao, 0) / activeDays.length)
          : 0;

        const consistencia = items.length > 0
          ? Math.min(100, Math.round((items.filter(i => i.hasActivity).length / items.length) * 100))
          : 0;

        return {
          name: label,
          date: mKey,
          label,
          books_read,
          pages_read,
          words_written,
          pronunciation_count,
          minutos: active_voice_duration,
          active_voice_duration,
          cards_reviewed,
          listening_count,
          total_quizzes_attempted,
          unique_words,
          shadowing_duration,
          shadowing_minutos: shadowing_duration,
          tempo_estimado,
          taxa_precisao,
          consistencia,
          total: books_read + pages_read + words_written + pronunciation_count + active_voice_duration + cards_reviewed + listening_count + total_quizzes_attempted + unique_words + shadowing_duration
        };
      });
    }

    return dailyItems.map(item => ({
      name: item.dateObj.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }),
      date: item.date,
      label: item.dateObj.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }),
      books_read: item.books_read,
      pages_read: item.pages_read,
      words_written: item.words_written,
      pronunciation_count: item.pronunciation_count,
      minutos: item.minutos,
      active_voice_duration: item.active_voice_duration,
      cards_reviewed: item.cards_reviewed,
      listening_count: item.listening_count,
      total_quizzes_attempted: item.total_quizzes_attempted,
      unique_words: item.unique_words,
      shadowing_duration: item.shadowing_duration,
      shadowing_minutos: item.shadowing_minutos,
      tempo_estimado: item.tempo_estimado,
      taxa_precisao: item.taxa_precisao,
      consistencia: item.consistencia,
      total: item.books_read + item.pages_read + item.words_written + item.pronunciation_count + item.active_voice_duration + item.cards_reviewed + item.listening_count + item.total_quizzes_attempted + item.unique_words + item.shadowing_duration
    }));
  }, [logs, dailyWordStatsMap, shadowingDailyMap, dailyTempoMap, dailyPrecisaoMap, dailyConsistenciaMap, periodAnalytics, timeframe]);

  const chartPageSize = 7;
  const totalChartPages = useMemo(() => {
    return Math.max(1, Math.ceil(chartData.length / chartPageSize));
  }, [chartData]);

  useEffect(() => {
    if (chartData.length > 0) {
      setChartPageIndex(Math.max(0, Math.ceil(chartData.length / chartPageSize) - 1));
    }
  }, [chartData.length]);

  const visibleChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    const start = chartPageIndex * chartPageSize;
    return chartData.slice(start, start + chartPageSize);
  }, [chartData, chartPageIndex]);

  const handleDateSelect = (dateStr: string) => {
    if (!dateStr || !chartData.length) return;
    setSelectedCalendarDate(dateStr);
    const foundIdx = chartData.findIndex(d => d.date === dateStr);
    if (foundIdx !== -1) {
      const page = Math.floor(foundIdx / chartPageSize);
      setChartPageIndex(page);
    }
  };

  const getExerciseTypeLabel = (type: string) => {
    if (!type) return 'QUIZZES';
    const lower = type.toLowerCase();
    if (lower === 'pronunciation' || lower === 'pronúncia') return 'PRONÚNCIA';
    if (lower === 'writing' || lower === 'escrita') return 'ESCRITA';
    if (lower === 'active_voice' || lower === 'voz_ativa') return 'VOZ ATIVA';
    if (lower === 'flashcards') return 'FLASHCARDS';
    if (lower === 'quizzes' || lower === 'quiz') return 'QUIZZES';
    if (lower === 'reading' || lower === 'leitura') return 'LEITURA';
    return type.toUpperCase();
  };

  const circularSegments = useMemo(() => {
    const keys = ['books_read', 'pages_read', 'unique_words', 'words_written', 'pronunciation_count', 'active_voice_duration', 'cards_reviewed', 'total_quizzes_attempted', 'listening_count'];
    const minAngle = 20;
    const gap = 10;
    const totalGap = keys.length * gap;
    const availableDeg = 360 - totalGap;

    const rawValues = keys.map(k => {
      const val = stats[k]?.current || 0;
      return k === 'active_voice_duration' ? (val > 0 ? val / 60 : 0) : val;
    });

    const totalSum = rawValues.reduce((a, b) => a + b, 0);

    let angles: number[] = [];
    if (totalSum === 0) {
      const equal = availableDeg / keys.length;
      angles = keys.map(() => equal);
    } else {
      const remDeg = Math.max(0, availableDeg - (keys.length * minAngle));
      angles = rawValues.map(v => minAngle + (remDeg > 0 ? (v / totalSum) * remDeg : 0));
    }

    let currentAngle = -90;
    const center = 170;
    const radius = 110;

    return keys.map((key, idx) => {
      const angle = angles[idx];
      const startAngle = currentAngle + (gap / 2);
      const endAngle = startAngle + angle;
      currentAngle += angle + gap;

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;
      const midRad = ((startAngle + endAngle) / 2 * Math.PI) / 180;

      const x1 = center + radius * Math.cos(startRad);
      const y1 = center + radius * Math.sin(startRad);
      const x2 = center + radius * Math.cos(endRad);
      const y2 = center + radius * Math.sin(endRad);

      const largeArc = angle > 180 ? 1 : 0;
      const path = `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;

      const nodeX = center + radius * Math.cos(midRad);
      const nodeY = center + radius * Math.sin(midRad);

      const unit = key === 'books_read' ? 'Livros' : key === 'pages_read' ? 'Páginas' : key === 'unique_words' ? 'Palavras' : key === 'words_written' ? 'Textos' : key === 'pronunciation_count' ? 'Áudios' : key === 'active_voice_duration' ? (stats[key]?.current > 3600 ? 'Horas' : 'Min.') : key === 'cards_reviewed' ? 'Cards' : key === 'listening_count' ? 'Exercícios' : 'Quizzes';

      return {
        key,
        config: metricConfig[key],
        value: rawValues[idx],
        formattedVal: metricConfig[key].isTime ? (stats[key]?.current > 0 ? (stats[key].current / 60).toFixed(1) : '0') : (stats[key]?.current || 0),
        unit,
        path,
        nodeX,
        nodeY
      };
    });
  }, [stats, metricConfig]);

  const totalAccumulatedCount = useMemo(() => {
    return Object.entries(stats).reduce((acc, [key, val]: any) => {
      const cur = val.current || 0;
      return acc + (key === 'active_voice_duration' ? Math.round(cur / 60) : cur);
    }, 0);
  }, [stats]);

  const reportMetrics = periodAnalytics;
  const masteredErrors = reportData?.mastered_errors || [];
  const pendingErrors = reportData?.pending_errors || [];
  const skillRadar = periodAnalytics.skill_radar;

  const getPayloadTitle = (item: any) => {
    try {
      let payload = item.item_payload_json;
      if (typeof payload === 'string') {
        payload = JSON.parse(payload);
      }
      if (payload) {
        if (payload.question) return payload.question;
        if (payload.word) return payload.word;
        if (payload.phrase) return payload.phrase;
        if (payload.front) return payload.front;
        if (payload.prompt) return payload.prompt;
        if (payload.original) return payload.original;
        if (payload.title) return payload.title;
      }
    } catch (e) {}
    if (item.last_user_input) return item.last_user_input;
    return 'Exercício';
  };

  const playWordAudio = (word: string) => {
    if (!word) return;
    textToSpeechService.speak(word, 'en-US');
  };

  const handleMetricSelect = (key: string) => {
    setActiveMetric(key);
    setViewMode('flow');
  };

  const handleCloseFlow = () => {
    setViewMode('summary');
    setActiveMetric('all');
  };

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen text-ink pb-24 pt-4">
      
      {/* Header with Filter Controls */}
      <header className="p-4 md:p-8 max-w-6xl mx-auto space-y-5">
        <AppBackButton to="/personal-hub" />
        <StudyPageHeader tourKey="reports" title={t('statistics.title', 'Relatórios')} />

        {/* Global Filter Bar (Período & Idioma) */}
        <div data-tour="reports-filters" className="flex flex-wrap items-center gap-2 w-full justify-between sm:justify-start">
          <div className="flex items-center gap-1 bg-surface border border-black/[0.06] dark:border-white/[0.06] p-1 rounded-full">
            {(['daily', 'weekly', 'monthly', 'total'] as const).map(p => (
              <button
                key={p}
                onClick={() => setTimeframe(p)}
                className={`h-9 px-4 rounded-full text-xs transition-colors duration-300 cursor-pointer ${
                  timeframe === p ? 'bg-ink text-surface' : 'text-muted hover:text-ink'
                }`}
              >
                {p === 'daily' ? 'Dia' : p === 'weekly' ? 'Semana' : p === 'monthly' ? 'Mês' : 'Total'}
              </button>
            ))}
          </div>

          <select 
            value={filterLang} 
            onChange={(e) => setFilterLang(e.target.value)}
            className="h-11 bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-full px-4 text-xs font-medium focus:outline-none focus:border-ink/25 transition-colors cursor-pointer"
          >
            <option value="all"><T k="online_library.all_languages" /></option>
            {availableLangs.map(l => (
              <option key={l.code} value={l.code}>{l.name.toUpperCase()}</option>
            ))}
          </select>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 space-y-10">

        {/* RETROSPECTIVA (design 2.0): faixa lavanda com orbe animado */}
        <div data-tour="reports-overview">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="group bg-tint-lavender rounded-[32px] p-6 md:p-8 text-ink relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-6"
          >
            <div aria-hidden className="absolute -top-16 -right-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
            <div aria-hidden className="absolute -bottom-24 left-1/3 w-56 h-56 bg-tint-sky/60 dark:bg-tintd-sky/10 rounded-full blur-3xl pointer-events-none" />

            <div className="flex items-center gap-5 relative z-10">
              <div className="relative w-16 h-16 shrink-0">
                <motion.span
                  aria-hidden
                  className="absolute -inset-1.5 rounded-full border border-dashed border-ink/25"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
                />
                <div className="w-16 h-16 rounded-full bg-ink flex items-center justify-center text-surface">
                  <AnimatedIcon motion="spin"><Sparkles size={26} strokeWidth={1.6} /></AnimatedIcon>
                </div>
              </div>
              <div>
                <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-ink/55">retrospectiva libritui</span>
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-[-0.03em] mt-0.5"><T k="ui.stats.evolution" /><span className="text-primary">.</span></h3>
                <p className="text-[13px] text-ink/65 mt-1"><T k="ui.stats.evolutionHint" /></p>
              </div>
            </div>

            <button
              onClick={() => setIsWrappedOpen(true)}
              className="group/btn relative z-10 w-full sm:w-auto h-14 pl-2 pr-6 bg-primary text-slate-900 rounded-full text-sm font-medium flex items-center justify-center gap-3 transition-transform hover:scale-[1.03] active:scale-95 cursor-pointer shrink-0"
            >
              <span className="w-10 h-10 rounded-full bg-surface/15 flex items-center justify-center">
                <Play size={15} fill="currentColor" className="ml-0.5 transition-transform duration-300 group-hover/btn:scale-110" />
              </span>
              Assistir retrospectiva
            </button>
          </motion.div>
        </div>

        {/* ORBES (igual à Home): cada um resume uma seção; tocar abre os detalhes abaixo */}
        {(() => {
          const skillVals = [skillRadar?.reading, skillRadar?.vocabulary, skillRadar?.active_voice, skillRadar?.writing, skillRadar?.pronunciation, skillRadar?.listening].map(v => Number(v) || 0);
          const skillAvg = skillVals.reduce((a, b) => a + b, 0) / skillVals.length;
          const errorsTotal = pendingErrors.length + masteredErrors.length;
          const compact = (n: number) => n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
          const orbs = [
            { id: 'summary' as const, label: 'Atividades', value: compact(totalAccumulatedCount), status: `${reportMetrics.equivalent_minutes || 0} min estudados`, pct: reportMetrics.consistency_percent || 0, tint: 'bg-tint-sky', stroke: 'rgb(var(--tintd-sky))', icon: <Activity size={17} strokeWidth={1.6} />, motion: 'pulse' as const },
            { id: 'skills' as const, label: 'Habilidades', value: `${Math.round(skillAvg)}%`, status: skillAvg > 0 ? 'Equilíbrio geral' : 'Comece a praticar', pct: skillAvg, tint: 'bg-tint-lavender', stroke: 'rgb(var(--tintd-lavender))', icon: <Compass size={17} strokeWidth={1.6} />, motion: 'spin' as const },
            { id: 'errors' as const, label: 'Erros', value: `${pendingErrors.length}`, status: pendingErrors.length > 0 ? 'Para revisar' : 'Tudo em dia', pct: errorsTotal ? (masteredErrors.length / errorsTotal) * 100 : 100, tint: 'bg-tint-rose', stroke: 'rgb(var(--tintd-rose))', icon: <Crosshair size={17} strokeWidth={1.6} />, motion: 'wobble' as const },
          ];
          return (
            <div data-tour="reports-detailed">
              <div className="grid grid-cols-3 gap-3 sm:gap-8 max-w-2xl mx-auto">
                {orbs.map((o, i) => (
                  <Orb key={o.id} {...o} index={i} open={openPanel === o.id} onClick={() => setOpenPanel(p => (p === o.id ? null : o.id))} />
                ))}
              </div>
              <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
                {openPanel ? 'Toque novamente para recolher' : 'Toque em um orbe para ver os detalhes'}
              </p>
            </div>
          );
        })()}

        <div ref={detailedSectionRef} data-tour="reports-focus">
          <AnimatePresence mode="wait">

            {/* ── RESUMO DE ATIVIDADES ─────────────────────────── */}
            {openPanel === 'summary' && (
              <motion.section
                key="panel-summary"
                id="section-detailed-stats"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <AnimatePresence mode="wait">
                  {viewMode === 'summary' ? (
                    <motion.div
                      key="summary-view"
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.3 }}
                      className="bg-surface rounded-[32px] p-6 md:p-10 border border-black/[0.06] dark:border-white/[0.06] space-y-8 relative"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">resumo de atividades</span>
                          <h3 className="font-display text-3xl font-medium text-ink tracking-[-0.03em] mt-1"><T k="ui.stats.practiced" /><span className="text-primary">.</span></h3>
                          <p className="text-[13px] text-muted mt-1"><T k="ui.stats.tapHint" /></p>
                        </div>

                        <div className="relative shrink-0">
                          <button
                            onClick={() => {
                              setTempLang(filterLang);
                              setTempPeriod(timeframe === 'daily' ? 'Dias' : timeframe === 'weekly' ? 'Semanas' : 'Anos');
                              setIsFilterOpen(true);
                            }}
                            className="w-11 h-11 rounded-full bg-primary/[0.05] hover:bg-primary hover:text-slate-900 text-ink transition-colors cursor-pointer flex items-center justify-center"
                            title="Filtrar"
                            aria-label="Filtrar"
                          >
                            <AnimatedIcon motion="bounce"><Filter size={17} strokeWidth={1.75} /></AnimatedIcon>
                          </button>
                          {isFilterOpen && typeof document !== 'undefined' && createPortal(
                            <div className="fixed inset-0 z-[99999999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setIsFilterOpen(false)}>
                              <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 flex flex-col gap-5 animate-in zoom-in-95 duration-200 w-full max-w-sm shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)]" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-center">
                                  <h4 className="font-display text-xl font-medium tracking-[-0.02em] text-ink">Filtros</h4>
                                  <button onClick={() => setIsFilterOpen(false)} className="w-9 h-9 rounded-full bg-ink/[0.05] hover:bg-ink/[0.1] text-ink flex items-center justify-center transition-colors"><X size={15} /></button>
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] uppercase font-mono text-muted tracking-[0.14em]">Agrupamento</label>
                                  <div className="bg-ink/[0.05] p-1 rounded-full flex items-center">
                                    {(['Dias', 'Semanas', 'Anos'] as const).map(f => (
                                      <button
                                        key={f}
                                        onClick={() => setTempPeriod(f)}
                                        className={`flex-1 h-9 rounded-full text-xs transition-colors ${tempPeriod === f ? 'bg-ink text-surface' : 'text-muted hover:text-ink'}`}
                                      >
                                        {f}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] uppercase font-mono text-muted tracking-[0.14em]">Idioma</label>
                                  <select
                                    value={tempLang}
                                    onChange={(e) => setTempLang(e.target.value)}
                                    className="w-full h-11 bg-ink/[0.04] border border-black/[0.06] dark:border-white/[0.06] rounded-full px-4 text-sm text-ink outline-none appearance-none cursor-pointer"
                                  >
                                    <option value="all"><T k="online_library.all_languages" /></option>
                                    {availableLangs.map((l: any) => (<option key={l.code} value={l.code}>{l.name}</option>))}
                                  </select>
                                </div>
                                <button
                                  onClick={() => {
                                    setFilterLang(tempLang);
                                    const mappedTf = tempPeriod === 'Dias' ? 'daily' : tempPeriod === 'Semanas' ? 'weekly' : 'yearly';
                                    setTimeframe(mappedTf);
                                    setIsFilterOpen(false);
                                  }}
                                  className="w-full mt-1 h-12 bg-primary text-slate-900 rounded-full text-sm font-medium transition-transform hover:scale-[1.01] active:scale-95 cursor-pointer"
                                >
                                  Aplicar filtros
                                </button>
                              </div>
                            </div>,
                            document.body
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col lg:flex-row items-center gap-10">

                        {/* Anel de atividades (cores do tema) */}
                        <div className="relative w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] flex items-center justify-center shrink-0">
                          <svg viewBox="0 0 340 340" className="w-full h-full select-none text-ink">
                            <circle cx="170" cy="170" r="110" fill="none" stroke="currentColor" strokeWidth="22" className="opacity-[0.06]" />

                            {circularSegments.map((seg, i) => (
                              <motion.path
                                key={seg.key}
                                d={seg.path}
                                fill="none"
                                stroke={seg.config.color}
                                strokeWidth={hoverMetric === seg.key ? 24 : 18}
                                strokeLinecap="round"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.9, delay: 0.1 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                                className="cursor-pointer transition-[stroke-width] duration-300"
                                onMouseEnter={() => setHoverMetric(seg.key)}
                                onMouseLeave={() => setHoverMetric(null)}
                                onClick={() => handleMetricSelect(seg.key)}
                              />
                            ))}

                            {circularSegments.map((seg) => (
                              <g
                                key={`node-${seg.key}`}
                                transform={`translate(${seg.nodeX}, ${seg.nodeY})`}
                                className="cursor-pointer"
                                onMouseEnter={() => setHoverMetric(seg.key)}
                                onMouseLeave={() => setHoverMetric(null)}
                                onClick={() => handleMetricSelect(seg.key)}
                              >
                                <circle r={hoverMetric === seg.key ? 19 : 16} fill="rgb(var(--surface))" stroke={seg.config.color} strokeWidth={2} className="transition-all duration-300" />
                                <foreignObject x={-8} y={-8} width={16} height={16} className="pointer-events-none overflow-visible">
                                  <div className="w-full h-full flex items-center justify-center" style={{ color: seg.config.color }}>
                                    <AnimatedIcon motion={metricMotion(seg.key)} active={hoverMetric === seg.key} playOnHover={false}>
                                      {metricIcon(seg.key, 13)}
                                    </AnimatedIcon>
                                  </div>
                                </foreignObject>
                              </g>
                            ))}

                            <text x="170" y="166" textAnchor="middle" fill="currentColor" style={{ fontSize: 44, fontWeight: 300, letterSpacing: '-0.05em' }} className="font-display">
                              {totalAccumulatedCount.toLocaleString('pt-BR')}
                            </text>
                            <text x="170" y="192" textAnchor="middle" fill="currentColor" style={{ fontSize: 10, letterSpacing: '0.14em', opacity: 0.5 }} className="font-mono uppercase">
                              {(hoverMetric && metricConfig[hoverMetric]?.label) || t('statistics.total_accumulated', 'atividades')}
                            </text>
                          </svg>
                        </div>

                        {/* Atividades: cada uma com o ícone da seção correspondente */}
                        <div className="flex-1 w-full grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                          {[
                            ...circularSegments.map(seg => ({ key: seg.key, value: seg.formattedVal, unit: seg.unit })),
                            { key: 'shadowing_duration', value: shadowingDisplay.val, unit: shadowingDisplay.unit },
                            { key: 'tempo_estimado', value: reportMetrics.equivalent_minutes || 0, unit: 'min' },
                            { key: 'taxa_precisao', value: reportMetrics.accuracy_rate || 0, unit: '%' },
                            { key: 'consistencia', value: reportMetrics.consistency_percent || 0, unit: '%' },
                          ].map((m, i) => {
                            const cfg = metricConfig[m.key];
                            const active = hoverMetric === m.key;
                            return (
                              <motion.button
                                key={`card-${m.key}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.05 + i * 0.03, ease: [0.22, 1, 0.36, 1] }}
                                onClick={() => handleMetricSelect(m.key)}
                                onHoverStart={() => setHoverMetric(m.key)}
                                onHoverEnd={() => setHoverMetric(null)}
                                className={`group p-3 rounded-[20px] text-left flex items-center gap-3 cursor-pointer transition-colors ${active ? 'bg-ink/[0.07] dark:bg-white/[0.07]' : 'bg-ink/[0.03] dark:bg-white/[0.03]'}`}
                              >
                                <span
                                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110"
                                  style={{ backgroundColor: `${cfg?.color}26`, color: cfg?.color }}
                                >
                                  <AnimatedIcon motion={metricMotion(m.key)} active={active} playOnHover={false}>{metricIcon(m.key, 17)}</AnimatedIcon>
                                </span>
                                <span className="min-w-0">
                                  <span className="block font-display text-lg font-medium text-ink tracking-[-0.03em] tabular-nums leading-tight">
                                    {m.value} <span className="text-xs text-muted font-sans tracking-normal">{m.unit}</span>
                                  </span>
                                  <span className="block text-[9px] uppercase font-mono tracking-[0.14em] text-muted truncate">{cfg?.label}</span>
                                </span>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="flow-view"
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.3 }}
                      className="bg-surface rounded-[32px] p-6 md:p-10 border border-black/[0.06] dark:border-white/[0.06] space-y-8 relative"
                    >
                      {/* Cabeçalho do fluxo */}
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={handleCloseFlow}
                            className="w-11 h-11 rounded-full bg-primary/[0.05] hover:bg-primary hover:text-slate-900 text-ink transition-colors cursor-pointer flex items-center justify-center shrink-0"
                            title="Fechar e voltar ao Resumo"
                            aria-label="Fechar e voltar ao Resumo"
                          >
                            <AnimatedIcon motion="spin"><X size={18} strokeWidth={1.75} /></AnimatedIcon>
                          </button>
                          <div>
                            <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> {t('statistics.activity_flow.subtitle')}
                            </span>
                            <h3 className="font-display text-2xl font-medium text-ink tracking-[-0.03em] mt-0.5">
                              {activeMetric !== 'all' && metricConfig[activeMetric] ? metricConfig[activeMetric].label.toLowerCase().replace(/^./, c => c.toUpperCase()) : t('statistics.activity_flow.title')}
                            </h3>
                          </div>
                        </div>

                        {/* Chips de atividades */}
                        <div className="flex flex-wrap gap-1.5 sm:max-w-[60%] sm:justify-end">
                          {Object.entries(metricConfig).map(([key, config]) => (
                            <button
                              key={key}
                              onClick={() => setActiveMetric(activeMetric === key ? 'all' : key)}
                              className={`group h-8 pl-1 pr-3 rounded-full text-[10px] uppercase font-mono tracking-[0.1em] flex items-center gap-1.5 transition-all cursor-pointer ${
                                activeMetric === key ? 'bg-ink text-surface' : activeMetric === 'all' ? 'bg-ink/[0.05] text-ink/75 hover:bg-ink/[0.09]' : 'bg-ink/[0.03] text-ink/35 hover:text-ink/70'
                              }`}
                            >
                              <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${config.color}30`, color: activeMetric === key ? undefined : config.color }}>
                                <AnimatedIcon motion={metricMotion(key)} active={activeMetric === key}>{metricIcon(key, 12)}</AnimatedIcon>
                              </span>
                              {config.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Gráfico */}
                      <div className="w-full h-[340px] min-h-[300px] relative">
                        <ResponsiveContainer width="100%" height={340} minWidth={1} minHeight={300}>
                          {activeMetric === 'all' ? (
                            <AreaChart data={visibleChartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                              <defs>
                                {Object.entries(metricConfig).map(([key, config]) => (
                                  <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={config.color} stopOpacity={0.28}/>
                                    <stop offset="95%" stopColor={config.color} stopOpacity={0}/>
                                  </linearGradient>
                                ))}
                              </defs>
                              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={true} dy={10} stroke="rgb(var(--muted))" fontSize={11} fontFamily="Geist Mono, monospace" />
                              <YAxis hide />
                              <Tooltip
                                content={({ active, payload, label }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <div className="bg-surface text-ink p-5 rounded-[22px] border border-black/[0.06] dark:border-white/[0.08] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] space-y-3 min-w-[200px]">
                                        <p className="text-[10px] uppercase font-mono tracking-[0.14em] text-muted border-b border-black/[0.06] dark:border-white/[0.06] pb-2">{label}</p>
                                        <div className="space-y-1.5">
                                          {[...payload].sort((a, b) => b.value - a.value).map((item: any, i: number) => {
                                            const keyName = item.dataKey === 'minutos' ? 'active_voice_duration' : item.dataKey === 'shadowing_minutos' ? 'shadowing_duration' : item.dataKey;
                                            const cfg = metricConfig[keyName];
                                            const unit = cfg?.isTime ? 'min' : (keyName === 'taxa_precisao' || keyName === 'consistencia' ? '%' : '');
                                            return (
                                              <div key={i} className="flex items-center justify-between gap-8">
                                                <div className="flex items-center gap-2" style={{ color: item.color }}>
                                                  {metricIcon(keyName, 12)}
                                                  <span className="text-[10px] uppercase tracking-[0.12em] font-mono text-ink/70">{cfg?.label || keyName}</span>
                                                </div>
                                                <span className="text-sm font-medium tabular-nums">{item.value} {unit}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              {Object.entries(metricConfig).map(([key, config]) => (
                                <Area
                                  key={key}
                                  type="monotone"
                                  dataKey={key === 'active_voice_duration' ? 'minutos' : key === 'shadowing_duration' ? 'shadowing_minutos' : key}
                                  stroke={config.color}
                                  strokeWidth={2.5}
                                  fillOpacity={1}
                                  fill={`url(#gradient-${key})`}
                                />
                              ))}
                            </AreaChart>
                          ) : (
                            <BarChart data={visibleChartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={true} dy={10} stroke="rgb(var(--muted))" fontSize={11} fontFamily="Geist Mono, monospace" />
                              <YAxis hide />
                              <Tooltip
                                cursor={{ fill: 'rgba(127,127,127,0.08)', radius: 12 }}
                                content={({ active, payload, label }) => {
                                  if (active && payload && payload.length) {
                                    const cfg = metricConfig[activeMetric];
                                    const unitStr = cfg?.isTime ? 'min ' : (activeMetric === 'taxa_precisao' || activeMetric === 'consistencia' ? '% ' : '');
                                    return (
                                      <div className="bg-surface text-ink p-4 rounded-[20px] border border-black/[0.06] dark:border-white/[0.08] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)]">
                                        <p className="text-[10px] uppercase font-mono tracking-[0.14em] text-muted mb-1">{label}</p>
                                        <p className="text-lg font-medium tabular-nums">{payload[0].value} {unitStr}<span className="text-xs text-muted">{cfg?.label}</span></p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Bar dataKey={activeMetric === 'active_voice_duration' ? 'minutos' : activeMetric === 'shadowing_duration' ? 'shadowing_minutos' : activeMetric} radius={[999, 999, 999, 999]} barSize={34}>
                                {visibleChartData.map((entry: any, index: number) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={metricConfig[activeMetric]?.color || '#5E7FA3'}
                                    fillOpacity={0.45 + (index / visibleChartData.length) * 0.55}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      </div>

                      {/* Paginação de 7 dias e calendário */}
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-5 border-t border-black/[0.06] dark:border-white/[0.06]">
                        <div className="text-[10px] uppercase font-mono tracking-[0.14em] text-muted">
                          Exibindo {visibleChartData.length} de {chartData.length} {timeframe === 'weekly' ? 'semanas registradas' : timeframe === 'yearly' ? 'meses registrados' : 'dias registrados'} (Página {chartPageIndex + 1} de {totalChartPages})
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setChartPageIndex(prev => Math.max(0, prev - 1))}
                            disabled={chartPageIndex === 0}
                            className="w-10 h-10 rounded-full bg-ink/[0.05] hover:bg-ink/[0.1] text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center"
                            title="Voltar 7 dias"
                          >
                            <AnimatedIcon motion="slide"><ChevronLeft size={16} strokeWidth={1.75} /></AnimatedIcon>
                          </button>

                          <div className="relative h-10 flex items-center gap-2 bg-ink/[0.05] rounded-full px-4 cursor-pointer hover:bg-ink/[0.09] transition-colors">
                            <Calendar size={14} strokeWidth={1.75} className="text-primary shrink-0 pointer-events-none" />
                            <input
                              type="date"
                              value={selectedCalendarDate}
                              onChange={(e) => handleDateSelect(e.target.value)}
                              className="bg-transparent text-xs text-ink focus:outline-none cursor-pointer"
                              title={i18nInstance.t('ui.stats.goToDate')}
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => setChartPageIndex(prev => Math.min(totalChartPages - 1, prev + 1))}
                            disabled={chartPageIndex >= totalChartPages - 1}
                            className="w-10 h-10 rounded-full bg-ink/[0.05] hover:bg-ink/[0.1] text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center"
                            title={i18nInstance.t('ui.stats.next7')}
                          >
                            <AnimatedIcon motion="slide"><ChevronRight size={16} strokeWidth={1.75} /></AnimatedIcon>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.section>
            )}

            {/* ── EQUILÍBRIO DE HABILIDADES ────────────────────── */}
            {openPanel === 'skills' && (
              <motion.section
                key="panel-skills"
                ref={focusSectionRef as any}
                id="section-focus-diagnosis"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[32px] p-6 md:p-10 space-y-8"
              >
                <div>
                  <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">perfil do estudante</span>
                  <h3 className="font-display text-3xl font-medium tracking-[-0.03em] mt-1"><T k="ui.reports.skills" /><span className="text-primary">.</span></h3>
                  <p className="text-[13px] text-muted mt-1"><T k="ui.stats.calcHint" /></p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { name: 'Leitura de livros', pct: skillRadar.reading || 0, section: 'reading' as const, tint: 'bg-tint-lavender', bar: 'bg-tintd-lavender' },
                    { name: 'Vocabulário e flashcards', pct: skillRadar.vocabulary || 0, section: 'flashcards' as const, tint: 'bg-tint-sky', bar: 'bg-tintd-sky' },
                    { name: 'Conversação (Voz Ativa)', pct: skillRadar.active_voice || 0, section: 'active_voice' as const, tint: 'bg-tint-sage', bar: 'bg-ok' },
                    { name: 'Prática de escrita', pct: skillRadar.writing || 0, section: 'writing' as const, tint: 'bg-tint-rose', bar: 'bg-tintd-rose' },
                    { name: 'Pronúncia fonética', pct: skillRadar.pronunciation || 0, section: 'pronunciation' as const, tint: 'bg-tint-peach', bar: 'bg-tintd-peach' },
                    { name: 'Compreensão auditiva', pct: skillRadar.listening || 0, section: 'listening' as const, tint: 'bg-tint-butter', bar: 'bg-warn' },
                  ].map((s, i) => {
                    const { Icon, motion: m } = SECTION_ICONS[s.section];
                    const active = hoverMetric === `skill-${s.section}`;
                    return (
                      <motion.div
                        key={s.name}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.05 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                        onHoverStart={() => setHoverMetric(`skill-${s.section}`)}
                        onHoverEnd={() => setHoverMetric(null)}
                        onTap={() => setHoverMetric(`skill-${s.section}`)}
                        className={`${s.tint} text-ink rounded-[22px] p-4 flex items-center gap-4`}
                      >
                        <span className="w-11 h-11 rounded-full bg-surface/70 dark:bg-black/20 flex items-center justify-center shrink-0">
                          <AnimatedIcon motion={m as any} active={active} playOnHover={false}><Icon size={18} strokeWidth={1.75} /></AnimatedIcon>
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm truncate">{s.name}</span>
                            <span className="font-display text-xl font-medium tracking-[-0.03em] tabular-nums">{s.pct}<span className="text-xs text-ink/50">%</span></span>
                          </div>
                          <div className="w-full h-1.5 bg-ink/[0.08] rounded-full overflow-hidden mt-2">
                            <motion.div
                              className={`h-full ${s.bar} rounded-full`}
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(100, s.pct)}%` }}
                              transition={{ duration: 1, delay: 0.2 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                            />
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.section>
            )}

            {/* ── ERROS & MAESTRIA ─────────────────────────────── */}
            {openPanel === 'errors' && (
              <motion.section
                key="panel-errors"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[32px] p-6 md:p-10 space-y-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted"><T k="ui.stats.exerciseHub" /></span>
                    <h3 className="font-display text-3xl font-medium tracking-[-0.03em] mt-1">Erros e maestria<span className="text-primary">.</span></h3>
                    <p className="text-[13px] text-muted mt-1"><T k="ui.stats.exerciseHubHint" /></p>
                  </div>
                  <span className="w-12 h-12 rounded-full bg-tint-butter text-ink flex items-center justify-center shrink-0">
                    <AnimatedIcon motion="swing"><Award size={20} strokeWidth={1.75} /></AnimatedIcon>
                  </span>
                </div>

                {/* Abas */}
                <div className="flex items-center gap-1 bg-ink/[0.05] p-1 rounded-full w-full sm:w-fit">
                  <button
                    onClick={() => setErrorTab('mastered')}
                    className={`flex-1 sm:flex-none h-10 px-5 text-sm rounded-full transition-colors cursor-pointer flex items-center justify-center gap-2 ${errorTab === 'mastered' ? 'bg-ink text-surface' : 'text-muted hover:text-ink'}`}
                  >
                    <AnimatedIcon motion="pop" active={errorTab === 'mastered'}><CheckCircle2 size={15} strokeWidth={1.75} /></AnimatedIcon>
                    Superados <span className="font-mono text-xs opacity-60">{masteredErrors.length}</span>
                  </button>
                  <button
                    onClick={() => setErrorTab('pending')}
                    className={`flex-1 sm:flex-none h-10 px-5 text-sm rounded-full transition-colors cursor-pointer flex items-center justify-center gap-2 ${errorTab === 'pending' ? 'bg-ink text-surface' : 'text-muted hover:text-ink'}`}
                  >
                    <AnimatedIcon motion="wobble" active={errorTab === 'pending'}><Crosshair size={15} strokeWidth={1.75} /></AnimatedIcon>
                    Pendentes <span className="font-mono text-xs opacity-60">{pendingErrors.length}</span>
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-2.5 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
                  {(errorTab === 'mastered' ? masteredErrors : pendingErrors).length === 0 ? (
                    <div className="sm:col-span-2 py-12 text-center">
                      <span className="w-14 h-14 rounded-full bg-tint-sage text-ink mx-auto flex items-center justify-center mb-3">
                        <AnimatedIcon motion="pop" active><CheckCircle2 size={24} strokeWidth={1.6} /></AnimatedIcon>
                      </span>
                      <p className="text-sm text-muted">{errorTab === 'mastered' ? 'Nenhuma palavra masterizada ainda no histórico.' : 'Nenhum erro pendente para este idioma!'}</p>
                    </div>
                  ) : (
                    (errorTab === 'mastered' ? masteredErrors : pendingErrors).map((errItem: any, i: number) => {
                      const titleText = getPayloadTitle(errItem);
                      const itemLang = (errItem.language || filterLang || 'en').toLowerCase();
                      const flagStr = LANGUAGE_FLAGS[itemLang] || '🌐';
                      const isPending = errorTab === 'pending';
                      return (
                        <motion.div
                          key={errItem.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35, delay: Math.min(i, 10) * 0.03 }}
                          className={`group p-3 pl-4 rounded-[20px] flex items-center justify-between gap-3 ${isPending ? 'bg-tint-rose/60 dark:bg-tint-rose/40' : 'bg-ink/[0.03] dark:bg-white/[0.03]'}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xl select-none">{flagStr}</span>
                            <div className="min-w-0">
                              <span className="text-sm text-ink block truncate">{titleText}</span>
                              <span className="text-[10px] uppercase font-mono tracking-[0.12em] text-ink/50">
                                {getExerciseTypeLabel(errItem.exercise_type)} · erros {errItem.error_count || 1}
                              </span>
                            </div>
                          </div>
                          {isPending ? (
                            <button
                              onClick={() => navigate('/correction')}
                              className="group/btn h-9 pl-4 pr-1 rounded-full bg-primary text-slate-900 text-xs flex items-center gap-2 cursor-pointer transition-transform hover:scale-105 active:scale-95 shrink-0"
                            >
                              Corrigir
                              <span className="w-7 h-7 rounded-full bg-surface/15 flex items-center justify-center"><ArrowRight size={13} className="transition-transform group-hover/btn:translate-x-0.5" /></span>
                            </button>
                          ) : (
                            <button
                              onClick={() => playWordAudio(titleText)}
                              className="w-9 h-9 rounded-full bg-primary/[0.05] hover:bg-primary hover:text-slate-900 text-ink flex items-center justify-center transition-colors cursor-pointer shrink-0"
                              title={i18nInstance.t('ui.common.listenPronunciation')}
                            >
                              <AnimatedIcon motion="wobble"><Volume2 size={16} strokeWidth={1.75} /></AnimatedIcon>
                            </button>
                          )}
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>

        {/* Link para Conquistas */}
        <motion.button
          type="button"
          onClick={() => navigate('/achievements')}
          whileHover={{ y: -2 }}
          className="w-full bg-tint-butter text-ink p-5 md:p-6 rounded-[28px] flex items-center justify-between gap-4 cursor-pointer group text-left"
        >
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 bg-surface/70 dark:bg-black/20 rounded-full flex items-center justify-center shrink-0">
              <AnimatedIcon motion="swing"><Award size={24} strokeWidth={1.6} /></AnimatedIcon>
            </span>
            <div>
              <h4 className="font-display text-xl font-medium tracking-[-0.02em]">{t('statistics.achievements.link_title')}</h4>
              <p className="text-[13px] text-ink/60">{t('statistics.achievements.link_subtitle')}</p>
            </div>
          </div>
          <span className="w-11 h-11 rounded-full bg-primary/[0.07] flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:text-slate-900 shrink-0">
            <ArrowRight size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </motion.button>

      </main>

      {/* Modal do Wrapped Retrospectiva */}
      <WrappedModal 
        isOpen={isWrappedOpen}
        onClose={() => setIsWrappedOpen(false)}
        reportData={reportData}
        period={timeframe === 'total' ? 'monthly' : timeframe}
        username={dbProfile?.username || profile?.username}
        extra={{
          wordsLearned: stats.unique_words?.current || 0,
          totalVocab: uniqueWordsCount,
          minutes: reportMetrics.equivalent_minutes || 0,
          voiceMinutes: Math.round((stats.active_voice_duration?.current || 0) / 60),
          pages: stats.pages_read?.current || 0,
          cards: stats.cards_reviewed?.current || 0,
          writing: stats.words_written?.current || 0,
          pronunciation: stats.pronunciation_count?.current || 0,
          quizzes: stats.total_quizzes_attempted?.current || 0,
          listening: stats.listening_count?.current || 0,
          accuracy: reportMetrics.accuracy_rate || 0,
          consistency: reportMetrics.consistency_percent || 0,
          skills: skillRadar,
        }}
      />
    </div>
  );
};

export default StatisticsPage;
